import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { addPayment } from '../../utils/receivables';

// 입금 기록 창 (명세 §4-2, 계약 §2·§3 — 2026-10-23)
// 기공소 대표가 직접 받은 돈을 적는다. 자동으로 확인하는 기능이 아님.
// 메모에는 환자 이름·치식을 적지 않도록 안내 (40자 제한, rules 에서도 40자 제한).

const METHODS = [
    ['transfer', '계좌이체'],
    ['cash', '현금'],
    ['card', '카드'],
    ['other', '기타'],
];

// 한국 시간 기준 오늘 'YYYY-MM-DD'
export const todayKst = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const DATE_RE = /^20\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;   // rules 와 같은 형식

function PaymentModal({ labId, partnerKey, partnerName, balance, onClose, onSaved }) {
    const [amountText, setAmountText] = useState('');
    const [paidAt, setPaidAt] = useState(todayKst());
    const [method, setMethod] = useState('transfer');
    const [memo, setMemo] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const savingRef = useRef(false);   // 이중 클릭 가드 (state 반영 전 두 번째 클릭 차단)

    const amount = Number(String(amountText).replace(/[^\d]/g, '')) || 0;

    const submit = async () => {
        if (savingRef.current) return;
        setError('');
        if (!Number.isInteger(amount) || amount <= 0) { setError('금액을 1원 이상으로 적어 주세요.'); return; }
        if (amount >= 100000000) { setError('금액이 너무 큽니다. 1억 원 미만으로 나눠 적어 주세요.'); return; }
        if (!DATE_RE.test(paidAt)) { setError('입금일을 다시 골라 주세요.'); return; }
        if (paidAt > todayKst()) { setError('입금일은 오늘 이후로 적을 수 없습니다.'); return; }
        if (memo.length > 40) { setError('메모는 40자까지 적을 수 있습니다.'); return; }
        savingRef.current = true;
        setSaving(true);
        try {
            const id = await addPayment({
                labId, partnerKey, partnerName: (partnerName || '').slice(0, 60),
                amount, paidAt, method, memo: memo.trim(),
            });
            if (onSaved) onSaved(id);
            onClose();
        } catch (e) {
            console.error('[입금 기록] 저장 실패:', e?.code || e?.message);
            setError('저장하지 못했습니다. 잠시 후 다시 눌러 주세요.');
            savingRef.current = false;
            setSaving(false);
        }
    };

    const label = { display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 };
    const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 15, border: '1px solid #CBD5E1', borderRadius: 8 };

    return (
        <div onClick={() => { if (!saving) onClose(); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()}
                style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 20px 40px rgba(0,0,0,0.18)' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0F172A', flex: 1 }}>입금 기록</h3>
                    <button onClick={onClose} disabled={saving} aria-label="닫기"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 4 }}>
                        <X size={18} />
                    </button>
                </div>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748B' }}>
                    {partnerName}{typeof balance === 'number' && balance > 0 ? ` · 지금 받을 돈 ${balance.toLocaleString()}원` : ''}
                </p>

                <div style={{ marginBottom: 14 }}>
                    <label style={label}>{'받은 금액 (원)'}</label>
                    <input inputMode="numeric" value={amount ? amount.toLocaleString() : ''} placeholder="예: 820,000"
                        onChange={e => setAmountText(e.target.value)} style={{ ...input, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} autoFocus />
                </div>
                <div style={{ marginBottom: 14 }}>
                    <label style={label}>{'입금일'}</label>
                    <input type="date" value={paidAt} max={todayKst()} onChange={e => setPaidAt(e.target.value)} style={input} />
                </div>
                <div style={{ marginBottom: 14 }}>
                    <label style={label}>{'받은 방법'}</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {METHODS.map(([v, t]) => (
                            <button key={v} type="button" onClick={() => setMethod(v)}
                                style={{ padding: '8px 14px', fontSize: 14, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
                                    border: `1px solid ${method === v ? '#4F46E5' : '#E2E8F0'}`,
                                    background: method === v ? '#EEF2FF' : '#fff', color: method === v ? '#4F46E5' : '#475569' }}>
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ marginBottom: 8 }}>
                    <label style={label}>{'메모 (선택)'}</label>
                    <input value={memo} maxLength={40} onChange={e => setMemo(e.target.value.slice(0, 40))}
                        placeholder="환자 이름·치식은 적지 마세요(거래 메모 전용)" style={input} />
                    <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 4, textAlign: 'right' }}>{`${memo.length}/40`}</div>
                </div>

                {error && <div style={{ fontSize: 13, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>{error}</div>}

                <p style={{ fontSize: 12, color: '#94A3B8', margin: '4px 0 14px' }}>{'입금은 직접 기록한 것만 반영됩니다. 잘못 적은 기록은 지우지 않고 [취소]로 남깁니다.'}</p>

                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={onClose} disabled={saving}
                        style={{ flex: 1, padding: '11px 0', fontSize: 15, fontWeight: 700, borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: '#475569', cursor: 'pointer' }}>
                        {'닫기'}
                    </button>
                    <button onClick={submit} disabled={saving || amount <= 0}
                        style={{ flex: 2, padding: '11px 0', fontSize: 15, fontWeight: 800, borderRadius: 8, border: 'none',
                            background: saving || amount <= 0 ? '#A5B4FC' : '#4F46E5', color: '#fff', cursor: saving ? 'wait' : 'pointer' }}>
                        {saving ? '저장 중…' : '기록하기'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PaymentModal;
